'use client';

import { useEffect, useRef, useState } from 'react';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { RecurrenceFilter } from '@/lib/transactions/types';
import { RECURRENCE_OPTIONS, type RecurrenceOption } from './transactionType';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: readonly RecurrenceFilter[];
  onChange: (next: RecurrenceFilter[]) => void;
}

function RecurrenceOptionRow({
  option,
  checked,
  onToggle,
}: {
  option: RecurrenceOption;
  checked: boolean;
  onToggle: (token: RecurrenceFilter) => void;
}) {
  const id = `recurrence-${option.value}`;
  const handleChange = () => onToggle(option.value);
  return (
    <Label
      htmlFor={id}
      className={cn(
        'flex h-14 cursor-pointer items-center justify-between gap-3 border-b border-divider px-2 last:border-b-0',
        checked && 'text-foreground',
      )}
    >
      <span className="flex min-w-0 items-center gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-surface-sunken">
          <option.Icon className="size-4 text-text-primary" weight="duotone" />
        </span>
        <span className="truncate text-sm font-medium">{option.label}</span>
      </span>
      <Checkbox id={id} checked={checked} onCheckedChange={handleChange} />
    </Label>
  );
}

// Recorrência filter (VER-259) — MULTI-select. Contas fixas / Assinaturas / Parcelamentos, the same
// three groups the Recorrentes page uses. Toggling live-applies (the list updates immediately) so the
// drawer stays open while several are picked; "Concluir" (or outside-tap / Escape) closes it. Unlike
// the batch-apply CategoryFilterDrawer, there is no pending state — the CTA only dismisses.
export function RecurrencePickerDrawer({ open, onOpenChange, value, onChange }: Props) {
  // `value` is URL-backed and lags a router round-trip, so a naive toggle recomputed from it would
  // drop earlier picks on rapid multi-select. Track the live set in a ref (updated synchronously so
  // successive toggles accumulate) with a state mirror driving the checkboxes for instant feedback.
  const [selected, setSelected] = useState<ReadonlySet<RecurrenceFilter>>(() => new Set(value));
  const selectedRef = useRef(selected);

  // Resync when the URL-backed value changes from outside (deep-link, nav, Limpar elsewhere).
  useEffect(() => {
    const fromUrl = new Set(value);
    selectedRef.current = fromUrl;
    setSelected(fromUrl);
  }, [value]);

  const apply = (next: Set<RecurrenceFilter>) => {
    selectedRef.current = next;
    setSelected(next);
    // Preserve option order for a stable URL / chip label.
    onChange(RECURRENCE_OPTIONS.map((o) => o.value).filter((v) => next.has(v)));
  };
  const toggle = (token: RecurrenceFilter) => {
    const next = new Set(selectedRef.current);
    if (next.has(token)) next.delete(token);
    else next.add(token);
    apply(next);
  };
  const clearAll = () => apply(new Set());
  const close = () => onOpenChange(false);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        data-tokens="v2"
        className="bg-surface px-4"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="flex flex-row items-center justify-between px-0">
          <DrawerTitle className="text-lg font-semibold text-text-primary">Recorrência</DrawerTitle>
          <DrawerDescription className="sr-only">
            Selecione um ou mais tipos de recorrência.
          </DrawerDescription>
          {selected.size > 0 && (
            <button
              type="button"
              className="cursor-pointer text-sm text-text-secondary hover:text-text-primary"
              onClick={clearAll}
            >
              Limpar
            </button>
          )}
        </DrawerHeader>

        <fieldset aria-label="Recorrência" className="flex flex-col">
          {RECURRENCE_OPTIONS.map((opt) => (
            <RecurrenceOptionRow
              key={opt.value}
              option={opt}
              checked={selected.has(opt.value)}
              onToggle={toggle}
            />
          ))}
        </fieldset>

        <div className="border-t border-border pt-3">
          <button
            type="button"
            onClick={close}
            className="h-12 w-full cursor-pointer rounded-xl bg-primary text-sm font-semibold text-primary-foreground hover:bg-primary-hover"
          >
            Concluir
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
